import classNames from 'clsx';
import { applyPatch } from 'fast-json-patch';
import produce from 'immer';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import CloseButton, { Variant } from '@swf/components/StarsModal/components/CloseButton';
import { FeatureFlag, Header } from '@swf/core/enums';
import useChangeCurrency from '@swf/core/hooks/useChangeCurrency';
import useFeature from '@swf/core/hooks/useFeature';
import usePrevious from '@swf/core/hooks/usePrevious';
import useRequest from '@swf/core/hooks/useRequest';
import useUserState from '@swf/core/hooks/useUserState';
import { select } from '@swf/core/store';
import type { SpinOfDayData } from '@swf/core/types/CasinoBonus';
import { BonusStatus } from '@swf/core/types/CasinoBonus';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import type { InstantBonus } from '@swf/core/types/InstantBonus';
import type { RewardData, StarsCoinReward } from '@swf/core/types/StarsCoinReward';
import { Category } from '@swf/core/types/StarsCoinReward';
import type { WidgetFC, WidgetInstanceProps } from '@swf/core/types/Widget';
import { hasAffordable, isInstantBonus, sortIB } from '@swf/core/utils/casino';
import type { CasinoMyRewardsWidgetServices } from '@swf/generated/types/widgets/CasinoMyRewardsWidget';

import styles from './CasinoMyRewardsWidget.scss';
import CategorySelector from './components/CategorySelector/CategorySelector';
import FreeSpins from './components/FreeSpins/FreeSpins';
import InstantBonuses from './components/InstantBonuses/InstantBonuses';
import { createTimestamp } from './components/RefreshButton/RefreshButton';
import Rewards from './components/Rewards/Rewards';
import SpinOfTheDay from './components/SpinOfTheDay/SpinOfTheDay';
import type WithClose from './components/WithClose';

interface FreeSpinsData {
  games: CasinoGame[];
  subscriptions: { [path: string]: string };
}

interface Data {
  ib?: InstantBonus[];
  rewards?: RewardData;
  freeSpins?: FreeSpinsData;
  spinOfTheDay?: SpinOfDayData;
}
export type Props = WidgetInstanceProps<unknown, CasinoMyRewardsWidgetServices, Data>;

interface LocationState {
  reward?: StarsCoinReward;
  success?: boolean;
}

interface BodyProps extends WithClose {
  category: Category;
  data: Data;
  refreshTimestamp: number;
  spinOfDayEnabled: boolean;
  onReload(): void;
}

const Body: React.FC<BodyProps> = ({
  category,
  close,
  data,
  refreshTimestamp,
  spinOfDayEnabled,
  onReload
}) => {
  const { rewards, freeSpins: { games = [] } = {}, ib = [], spinOfTheDay } = data;

  switch (category) {
    case Category.FSB:
      return <FreeSpins games={games} close={close} />;
    case Category.Rewards:
      return <Rewards rewards={rewards?.rewards} close={close} />;
    case Category.IB:
      return (
        <InstantBonuses
          ib={ib}
          refreshTimestamp={refreshTimestamp}
          onReload={onReload}
          close={close}
        />
      );
    case Category.SpinOfDay:
      return spinOfDayEnabled ? <SpinOfTheDay close={close} data={spinOfTheDay} /> : null;
  }
};

export const MAX_SET_TIMEOUT = 0x7fffffff;

const CasinoMyRewardsWidget: WidgetFC<Props> = ({
  data = {},
  style,
  services,
  fetchService,
  updateState
}) => {
  const balance = useSelector(select.account.getStarsCoinBalance);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const isPlayMoney = useSelector(select.currency.isPlayMoney);
  const [refreshTimestamp, setRefreshTimestamp] = useUserState(createTimestamp());
  const [selected, setSelected] = useUserState<Category | undefined | null>(undefined);
  const fsbEnabled = useFeature(FeatureFlag.CasinoFreeSpinsToken);
  const ibEnabled = useFeature(FeatureFlag.CasinoInstantBonusesToken);
  const storeEnabled = useFeature(FeatureFlag.CasinoRewardsStoreToken);
  const spinOfDayEnabled = useFeature(FeatureFlag.CasinoSpinOfDay) && !isPlayMoney;

  const { rewards, freeSpins, ib, spinOfTheDay } = data;
  const isMobile = useSelector(select.device.isMobile);
  const currentModal = useSelector(select.modal.getCurrent);
  const [expiredSpins, setExpiredSpins] = useState<boolean>(false);
  const oldBalance = usePrevious(balance);

  const serviceFSB = services.freeSpins?.config.onAuth;
  const serviceIB = services.ib?.config.onAuth;
  const serviceRewards = services.rewards?.config.onAuth;
  const serviceSpinOfDay = services.spinOfTheDay?.config?.onAuth;

  const request = useRequest();

  const performIBUpdate = useCallback(
    (timestamp = createTimestamp()) => {
      setRefreshTimestamp(timestamp);
      serviceIB && fetchService('ib');
    },
    [fetchService, serviceIB, setRefreshTimestamp]
  );

  const available = Object.values(Category).filter((category) => {
    switch (category) {
      case Category.FSB:
        return freeSpins?.games?.length && serviceFSB && fsbEnabled;
      case Category.Rewards:
        return (
          balance > 0 &&
          serviceRewards &&
          !!rewards?.rewards.length &&
          hasAffordable({ rewards: rewards?.rewards, balance }) &&
          storeEnabled
        );
      case Category.IB:
        return !!ib?.length && serviceIB && ibEnabled;
      case Category.SpinOfDay:
        return (
          (spinOfDayEnabled && spinOfTheDay?.status === BonusStatus.Available) ||
          (spinOfTheDay?.status === BonusStatus.Playing &&
            spinOfTheDay?.numberOfTickets &&
            serviceSpinOfDay &&
            !expiredSpins)
        );
      default:
        return null;
    }
  });

  useEffect(() => {
    if (
      available.length &&
      (freeSpins !== undefined || !serviceFSB) &&
      (ib !== undefined || !serviceIB) &&
      (rewards !== undefined || !serviceRewards) &&
      (spinOfTheDay !== undefined || !serviceSpinOfDay || !spinOfDayEnabled) &&
      (selected === undefined ||
        (available.findIndex((item) => item === selected) === -1 && selected !== null))
    ) {
      setSelected(available[0]);
    }
  }, [
    available,
    freeSpins,
    ib,
    rewards,
    spinOfTheDay,
    selected,
    serviceFSB,
    serviceIB,
    serviceRewards,
    serviceSpinOfDay,
    spinOfDayEnabled,
    setSelected
  ]);

  useEffect(() => {
    if (currentModal) {
      const { path } = currentModal;
      const location = currentModal.locationState as LocationState;

      if (path === '/casino/buy-reward') {
        if (location.success && isInstantBonus(location.reward?.type)) {
          const now = createTimestamp();
          if (now - refreshTimestamp > 5) {
            performIBUpdate(now);
          }
        }
      }
    }
  }, [fetchService, selected, currentModal, refreshTimestamp, performIBUpdate]);

  useEffect(() => {
    if (oldBalance !== balance) {
      serviceRewards && fetchService('rewards');
      if (available.indexOf(Category.IB) !== -1) {
        serviceIB && fetchService('ib');
      }
    }
  }, [balance, fetchService, available, oldBalance, serviceIB, serviceRewards]);

  useChangeCurrency(() => fetchService('freeSpins'));

  useEffect(() => {
    if (!isAuthenticated) {
      updateState('spinOfTheDay', undefined);
      updateState('freeSpins', undefined);
    } else if (isAuthenticated && hasClientSession) {
      const id = setTimeout(() => {
        request({ service: 'casino.player.activebonus' });
      }, 1000);
      return () => clearTimeout(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, hasClientSession]);

  useEffect(() => {
    if (spinOfTheDay?.expiryTime && !expiredSpins) {
      const trigger = Math.min(MAX_SET_TIMEOUT, spinOfTheDay.expiryTime - new Date().getTime());
      const id = setTimeout(() => setExpiredSpins(true), trigger);

      return () => clearTimeout(id);
    }
  }, [expiredSpins, spinOfTheDay]);

  const updateSelected = useCallback(
    (theCategory: Category) => {
      setSelected(theCategory);
      if (theCategory === Category.IB) {
        performIBUpdate();
      }
    },
    [setSelected, performIBUpdate]
  );

  if (selected != null && !available.includes(selected)) {
    setSelected(available.length > 0 ? available[0] : null);
  }

  return isAuthenticated && available.length ? (
    <section className={classNames(styles.container, isMobile && styles.mobile)} style={style}>
      <Vary by={[Header.Country, Header.Currency]} />
      <CategorySelector onSelect={updateSelected} selected={selected} available={available} />
      {selected && (
        <Body
          category={selected}
          close={
            <CloseButton
              variant={Variant.Light}
              className={styles.closeButton}
              onClick={() => setSelected(null)}
            />
          }
          data={data}
          refreshTimestamp={refreshTimestamp}
          onReload={performIBUpdate}
          spinOfDayEnabled={spinOfDayEnabled}
        />
      )}
    </section>
  ) : null;
};

CasinoMyRewardsWidget.serviceReducers = {
  bonuses: (state) => state,
  spinOfTheDay: (state, { data }) =>
    produce(state, (draft = {}) => {
      draft.spinOfTheDay = draft.spinOfTheDay || ({} as SpinOfDayData);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data?.spinOfTheDay.forEach((patch: any) => {
        applyPatch(draft.spinOfTheDay, [patch]);
      });

      if (data?.spinOfTheDay.length === 0) {
        draft.spinOfTheDay = {} as SpinOfDayData;
      }
      return draft;
    }),
  ib: (state: Data = {}, { data }) => {
    const ibRaw = data.ibs;
    if (data.msg || !ibRaw) {
      return state;
    } else {
      return {
        ...state,
        ib: ibRaw.sort(sortIB)
      };
    }
  }
};

export default CasinoMyRewardsWidget;
